import React, { useState } from 'react'
import { Trans } from '@lingui/macro'

import { useFeatureFlags } from 'front-end/src/app/hooks/useFeatureFlags'
import {
  CampaignMessageTypeEnum,
  OrganisationModelAsAdminQueries
} from 'front-end/src/graphql/private/PrivateSchemaTypes'

import { Checkbox } from '../../../components/common/form/checkbox/Checkbox'
import DisableCampaignWithMessageActionModal from './DisableCampaignWithMessageActionModal'
import { AccountTabSection } from '../TabContentSection'
import { useSlackIntegrationUrlMutation } from '../operations/Integrations.codegen'
import { useMessageActionsRelatedCampaignsQuery } from '../operations/messageActionRelatedCampaigns.codegen'
import { SenderDetails } from './SenderDetails'
import { CustomSMTP } from './CustomSMTP'

export type INotificationTabForm = Pick<
  OrganisationModelAsAdminQueries,
  'email_comments' | 'email_content' | 'notifications'
>

interface IProps {
  form: INotificationTabForm
  onFormChange: (form: INotificationTabForm) => Promise<void>
}

export interface ICampaignDataState {
  id: number
  name?: string | null
  author?: {
    id: number
    email: string
  }
}

const NotificationsTab = ({ onFormChange, form }: IProps) => {
  const { featureFlags } = useFeatureFlags()

  const [smsData, setSMSData] = useState<ICampaignDataState[]>([])
  const [slackData, setSlackData] = useState<ICampaignDataState[]>([])
  const [msTeamsData, setMsTeamsData] = useState<ICampaignDataState[]>([])

  const [slackIntegrationUrl] = useSlackIntegrationUrlMutation()
  const {
    // TODO Make it lazy, it's loading huge query on backend
    data: slackMessageActionData,
    refetch: slackMessageActionRefetch
  } = useMessageActionsRelatedCampaignsQuery({
    variables: { type: CampaignMessageTypeEnum.SLACK }
  })
  const {
    // TODO Make it lazy, it's loading huge query on backend
    data: smsMessageActionData,
    refetch: smsMessageActionRefetch
  } = useMessageActionsRelatedCampaignsQuery({
    variables: { type: CampaignMessageTypeEnum.SMS }
  })
  const {
    // TODO Make it lazy, it's loading huge query on backend
    data: msteamsMessageActionData,
    refetch: msteamsMessageActionRefetch
  } = useMessageActionsRelatedCampaignsQuery({
    variables: { type: CampaignMessageTypeEnum.MSTEAMS }
  })

  const handleSlackIntegration = async (e) => {
    if (e.target.checked) {
      const { data } = await slackIntegrationUrl()

      if (data?.admin?.organisation?.slackIntegrationUrl) {
        window.open(data.admin.organisation.slackIntegrationUrl, '_self')
      }
    } else {
      if (
        slackMessageActionData?.admin?.organisation?.getCampaignsByActionType
          ?.length
      ) {
        return setSlackData(
          slackMessageActionData?.admin?.organisation
            ?.getCampaignsByActionType as ICampaignDataState[]
        )
      }
      onFormChange({
        ...form,
        notifications: {
          ...form.notifications,
          slack: {
            organisation_access_token: '',
            slack_access_token: ''
          }
        }
      })
    }
  }

  const handleSMSMessaging = (e) => {
    if (!e.target.checked) {
      if (
        smsMessageActionData?.admin?.organisation?.getCampaignsByActionType
          ?.length
      ) {
        return setSMSData(
          smsMessageActionData?.admin?.organisation
            ?.getCampaignsByActionType as ICampaignDataState[]
        )
      }
    }

    onFormChange({
      ...form,
      notifications: {
        ...form.notifications,
        sms: e.target.checked
      }
    })
  }

  const handleMsTeamsMessaging = (e) => {
    if (!e.target.checked) {
      if (
        msteamsMessageActionData?.admin?.organisation?.getCampaignsByActionType
          ?.length
      ) {
        return setMsTeamsData(
          msteamsMessageActionData?.admin?.organisation
            ?.getCampaignsByActionType as ICampaignDataState[]
        )
      }
    }

    onFormChange({
      ...form,
      notifications: {
        ...form.notifications,
        msteams: e.target.checked
      }
    })
  }

  return (
    <AccountTabSection className="NotificationTab Tab">
      <div className="AccountPage__box">
        <h4>
          <Trans>Comments email</Trans>
        </h4>
        <p className="text-description">
          <Trans>
            Notifies your people of any new comments on resources they are
            following. Maximum 1 email per day.
          </Trans>
        </p>
        <div className="AccountPage__checkBoxWrapper">
          <Checkbox
            id="NotificationTab__email_comments"
            isChecked={form.email_comments!}
            onChange={(event) =>
              onFormChange({
                ...form,
                email_comments: event.target.checked
              })
            }
          >
            <Trans>Enable comments email</Trans>
          </Checkbox>
        </div>
      </div>
      <div className="AccountPage__box">
        <h4>
          <Trans>New content email</Trans>
        </h4>
        <p className="text-description">
          <Trans>
            Notifies your people of newly published resources available for them
            to access. Maximum 1 email per week.
          </Trans>
        </p>
        <div className="AccountPage__checkBoxWrapper">
          <Checkbox
            id="NotificationTab__email_content"
            isChecked={form.email_content!}
            onChange={(event) =>
              onFormChange({
                ...form,
                email_content: event.target.checked
              })
            }
          >
            <Trans>Enable new content email</Trans>
          </Checkbox>
        </div>
      </div>

      {featureFlags?.custom_smtp && (
        <div className="AccountPage__box">
          <CustomSMTP />
        </div>
      )}

      {(featureFlags?.custom_email_sender_address ||
        featureFlags?.multiple_senders) && (
        <div className="AccountPage__box">
          <SenderDetails />
        </div>
      )}

      {featureFlags?.Slack && (
        <div className="AccountPage__box">
          <h4>Slack</h4>
          <p className="text-description">
            <Trans>
              Send Slack notifications direct to your people via campaigns.
              Requires the installation and approval of the Looop Slack App into
              your company Slack workspace. Once enabled you will be redirected
              to the Slack permissions page.
            </Trans>
          </p>
          <div className="AccountPage__checkBoxWrapper">
            <Checkbox
              isChecked={
                form.notifications &&
                form.notifications.slack &&
                !!form.notifications.slack.slack_access_token
              }
              onChange={handleSlackIntegration}
            >
              <Trans>Enable Slack messaging</Trans>
            </Checkbox>
          </div>
        </div>
      )}
      {featureFlags?.sms && (
        <div className="AccountPage__box">
          <h4>SMS</h4>
          <p className="text-description">
            <Trans>
              Send SMS notifications direct to your people via campaigns.
              Requires user to have their mobile phone number added into our
              system.
            </Trans>
          </p>
          <div className="AccountPage__checkBoxWrapper">
            <Checkbox
              id="NotificationTab__sms_messaging"
              isChecked={form.notifications && form.notifications.sms}
              onChange={handleSMSMessaging}
            >
              <Trans>Enable SMS messaging</Trans>
            </Checkbox>
          </div>
        </div>
      )}
      {featureFlags?.msteams && (
        <div className="AccountPage__box">
          <h4>
            <Trans>Microsoft Teams</Trans>
          </h4>
          <p className="text-description">
            <Trans>
              Send Microsoft Teams notifications direct to your people via
              campaigns.
            </Trans>
          </p>
          <div className="AccountPage__checkBoxWrapper">
            <Checkbox
              id="NotificationTab__msteams_messaging"
              isChecked={form.notifications && form.notifications.msteams}
              onChange={handleMsTeamsMessaging}
            >
              <Trans>Enable Microsoft Teams</Trans>
            </Checkbox>
          </div>
        </div>
      )}

      {smsData && (
        <DisableCampaignWithMessageActionModal
          data={smsData}
          show={smsData.length > 0}
          onHide={() => {
            setSMSData([])
          }}
          onDisable={() => {
            onFormChange({
              ...form,
              notifications: {
                ...form.notifications,
                sms: false
              }
            })
            smsMessageActionRefetch()
            setSMSData([])
          }}
          type={CampaignMessageTypeEnum.SMS}
        />
      )}

      <DisableCampaignWithMessageActionModal
        data={slackData}
        show={slackData.length > 0}
        onHide={() => {
          setSlackData([])
        }}
        onDisable={() => {
          onFormChange({
            ...form,
            notifications: {
              ...form.notifications,
              slack: {
                organisation_access_token: '',
                slack_access_token: ''
              }
            }
          })
          slackMessageActionRefetch()
          setSlackData([])
        }}
        type={CampaignMessageTypeEnum.SLACK}
      />

      {msTeamsData && (
        <DisableCampaignWithMessageActionModal
          data={msTeamsData}
          show={msTeamsData.length > 0}
          onHide={() => {
            setMsTeamsData([])
          }}
          onDisable={() => {
            onFormChange({
              ...form,
              notifications: {
                ...form.notifications,
                msteams: false
              }
            })
            msteamsMessageActionRefetch()
            setMsTeamsData([])
          }}
          type={CampaignMessageTypeEnum.MSTEAMS}
        />
      )}
    </AccountTabSection>
  )
}

export default NotificationsTab
